import { useTheme } from '@mui/material/styles';
import React, { FC } from 'react';
import { IOption as IRadioGroupOption } from '@/components/radiogroup-control/contract';
import { RadioGroupControl } from '@/components/radiogroup-control/radiogroup-control';
import { IProps, IOption } from './contract';

/**
 * ButtonGroupControl is an layer ontop of RadioGroupControl Component.
 * Simpler API to provide component rendered istead of default <Radio /> icon.
 *
 * @link https://github.com/subpublic/joinup/tree/development/client/components/radiogroup-control
 *
 * @example
 * <ButtonGroupControl
 *   control={control}
 *   label="Horizontal alignment"
 *   name="alignment.horizontal"
 *   defaultValue={alignment.horizontal}
 *   options={[
 *     {
 *       label: 'Left',
 *       value: ALIGNMENT_OPTIONS.start,
 *       component: <Icon>format_align_left</Icon>,
 *     },
 *     {
 *       label: 'Center',
 *       value: ALIGNMENT_OPTIONS.center,
 *       component: <Icon>format_align_justify</Icon>,
 *     },
 *     {
 *       label: 'Right',
 *       value: ALIGNMENT_OPTIONS.end,
 *       component: <Icon>format_align_right</Icon>,
 *     },
 *   ]}
 * />
 */
export const ButtonGroupControl: FC<Omit<IProps, 'onChange' | 'value'>> = ({
  options,
  ...props
}) => {
  const theme = useTheme();

  const mergeOptions = (options: IOption[]) =>
    options.map(
      (option): IRadioGroupOption => ({
        label: option.label,
        value: option.value,
        radioProps: {
          icon: option.component,
          checkedIcon: option.component,
          disableRipple: true,
          sx: {
            borderRadius: 0,
            '&.Mui-checked': {
              bgcolor: theme.palette.grey[200],
              color: '#78AF36'
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        },
      })
    );

  return (
    <RadioGroupControl
      row
      withTooltip
      options={mergeOptions(options)}
      sx={{
        '& .MuiFormLabel-root': {
          pb: 1,
        },
        '& .MuiRadioGroup-root': {
          '& span:nth-child(1) > label > span': {
            borderRadius: '5px 0 0 5px',
          },
          '& span:nth-last-child(1) > label > span': {
            borderRadius: '0 5px 5px 0',
          },
        },
        '& .MuiFormControlLabel-root': {
          m: 0,
        },
        '& .MuiFormControlLabel-label': {
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        },
      }}
      {...props}
    />
  );
};
